import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { pdpmCmgEstimatorNewTreatment } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { PdpmEstimate } from 'src/services/documentService/pdpmEstimate';
import PdpmEstimateDrawer from './pdpm-estimate-drawer';
import {
  ADVISORY_CHIP_TEXT,
  formatCompleteness,
  formatTargetMinutes,
  hasNonTherapyEoc,
  isSuppressed,
  overrideCount,
  resolveCmgOption,
  sortWarnings,
  warningSeverity,
} from './pdpm-estimator-utils';
import { usePdpmEstimate } from './use-pdpm-estimate';

/**
 * BRK-1967: advisory PDPM CMG estimate panel on the Create/Edit MDS Assessment
 * form. Read-only — it never writes an assessment and is not a grouper output;
 * the manual CMG dropdowns below it stay authoritative and are never blocked by
 * this panel's state.
 *
 * Mounted from `therapyPlanningEditDetails.tsx`, which serves both entry points
 * (patient-details MDS tab and the Therapy Planning module).
 */

// re-exported for call sites and tests that import it from the panel
export { ADVISORY_CHIP_TEXT };

/** how many warnings render as Alerts before collapsing into "+N more" */
const MAX_VISIBLE_WARNINGS = 2;

export type PdpmEstimatePanelProps = {
  patientUuid: string | undefined;
  /** 'YYYY-MM-DD', or null when the form has no ARD picked yet */
  ard: string | null;
  /** the facility's PT & OT dropdown labels, e.g. 'TB - 300 mins' */
  ptOtOptions: string[];
  slpOptions: string[];
  /**
   * Apply-mode writes (BRK-1970). Called with the resolved facility option label
   * verbatim, so what lands in the form is byte-identical to a manual pick. Omit
   * one to hide that discipline's apply button.
   */
  onApplyPtOt?: (optionLabel: string) => void;
  onApplySlp?: (optionLabel: string) => void;
  /** shown in the inputs drawer's subline */
  patientName?: string | null;
};

/**
 * Flag gate. `useFeatureToggleWhenReady` returns null until Split resolves, so
 * rendering nothing until it is explicitly `true` avoids flashing the panel at
 * facilities that do not have the estimator. Keeping the gate outside the inner
 * component also means the estimate is never fetched while the flag is off.
 */
export default function PdpmEstimatePanel(props: PdpmEstimatePanelProps) {
  const [isEstimatorEnabled] = useFeatureToggleWhenReady(pdpmCmgEstimatorNewTreatment, 'FACILITY');
  if (isEstimatorEnabled !== true) {
    return null;
  }
  return <PdpmEstimatePanelBody {...props} />;
}

function PdpmEstimatePanelBody({
  patientUuid,
  ard,
  ptOtOptions,
  slpOptions,
  patientName,
  onApplyPtOt,
  onApplySlp,
}: PdpmEstimatePanelProps) {
  const {
    estimate,
    overrides,
    loading,
    recalculating,
    error,
    refresh,
    setOverride,
    clearOverride,
    resetOverrides,
  } = usePdpmEstimate(patientUuid, ard);
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  // a non-therapy EOC has no PDPM estimate to give. The form's route state does
  // not carry `isNonTherapyPatient`, so the backend warning is the authoritative
  // signal and the panel removes itself once it arrives.
  if (hasNonTherapyEoc(estimate)) {
    return null;
  }

  return (
    <Card sx={{ p: 2 }}>
      <PanelHeader
        estimate={estimate}
        recalculating={recalculating}
        onRefresh={refresh}
        refreshDisabled={loading || !ard}
      />
      {recalculating && <LinearProgress sx={{ mb: 2 }} />}
      <PanelBody
        ard={ard}
        estimate={estimate}
        loading={loading}
        recalculating={recalculating}
        error={error}
        onRefresh={refresh}
        onViewInputs={() => setDrawerOpen(true)}
        overrideCount={overrideCount(overrides)}
        ptOtOptions={ptOtOptions}
        slpOptions={slpOptions}
        onApplyPtOt={onApplyPtOt}
        onApplySlp={onApplySlp}
      />
      <PdpmEstimateDrawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        estimate={estimate}
        patientName={patientName}
        ard={ard}
        overrides={overrides}
        recalculating={recalculating}
        onOverrideChange={setOverride}
        onClearOverride={clearOverride}
        onResetOverrides={resetOverrides}
      />
    </Card>
  );
}

function PanelHeader({
  estimate,
  recalculating,
  onRefresh,
  refreshDisabled,
}: {
  estimate: PdpmEstimate | null;
  recalculating: boolean;
  onRefresh: () => void;
  refreshDisabled: boolean;
}) {
  const completeness = formatCompleteness(estimate?.completeness);
  const meta = estimate?.meta;
  const metaParts = [
    meta?.fiscalYear ? `FY ${meta.fiscalYear}` : null,
    meta?.mappingVersion ? `Mapping ${meta.mappingVersion}` : null,
    meta?.calculatedAt ? `Calculated ${new Date(meta.calculatedAt).toLocaleString()}` : null,
  ].filter(Boolean);

  return (
    <Stack
      spacing={1}
      sx={{ mb: 2 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="subtitle1">PDPM Estimate</Typography>
        {/* the advisory framing is load-bearing, not decoration: it must be on
            screen in every state the panel renders, including errors */}
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={ADVISORY_CHIP_TEXT}
        />
        <Box sx={{ flexGrow: 1 }} />
        {completeness && (
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {completeness}
          </Typography>
        )}
        <Tooltip title="Recalculate the estimate">
          <span>
            <IconButton
              size="small"
              aria-label="Refresh PDPM estimate"
              onClick={onRefresh}
              disabled={refreshDisabled || recalculating}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {metaParts.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {metaParts.join(' · ')}
        </Typography>
      )}
    </Stack>
  );
}

function PanelBody({
  ard,
  estimate,
  loading,
  recalculating,
  error,
  onRefresh,
  onViewInputs,
  overrideCount: pendingOverrides,
  ptOtOptions,
  slpOptions,
  onApplyPtOt,
  onApplySlp,
}: {
  ard: string | null;
  estimate: PdpmEstimate | null;
  loading: boolean;
  recalculating: boolean;
  error: string | null;
  onRefresh: () => void;
  onViewInputs: () => void;
  overrideCount: number;
  ptOtOptions: string[];
  slpOptions: string[];
  onApplyPtOt?: (optionLabel: string) => void;
  onApplySlp?: (optionLabel: string) => void;
}) {
  if (!ard) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        Set the Assessment Reference Date to see the PDPM estimate
      </Typography>
    );
  }

  if (loading) {
    return (
      <Grid
        container
        spacing={2}
      >
        {[0, 1].map((tile) => (
          <Grid
            item
            xs={12}
            md={6}
            key={tile}
          >
            <Skeleton
              variant="rounded"
              height={96}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  // an error with no estimate to fall back on is the whole body; a failed
  // recalc keeps the last good estimate below the alert
  if (error && !estimate) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            size="small"
            color="inherit"
            onClick={onRefresh}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!estimate) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {error && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={onRefresh}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {/* the prior estimate stays readable while a recalculation is in flight,
          dimmed so it is clearly not yet the answer for the pending edit */}
      <Box sx={{ opacity: recalculating ? 0.5 : 1 }}>
        {isSuppressed(estimate) ? (
          <SuppressedState />
        ) : (
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <PtOtTile
                estimate={estimate}
                ptOtOptions={ptOtOptions}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
            >
              <SlpTile
                estimate={estimate}
                slpOptions={slpOptions}
              />
            </Grid>
          </Grid>
        )}
      </Box>
      {!isSuppressed(estimate) && (onApplyPtOt || onApplySlp) && (
        <ApplyModeFooter
          estimate={estimate}
          ptOtOptions={ptOtOptions}
          slpOptions={slpOptions}
          onApplyPtOt={onApplyPtOt}
          onApplySlp={onApplySlp}
        />
      )}
      <WarningList
        estimate={estimate}
        onViewInputs={onViewInputs}
        overrideCount={pendingOverrides}
      />
    </Stack>
  );
}

export const APPLY_UNRESOLVABLE_TOOLTIP = 'Facility case-mix minutes not loaded — pick manually';

/**
 * BRK-1970 apply mode. Off on every mount by design — `useState(false)` with no
 * persistence anywhere, so filling a clinical field is always a deliberate act in
 * the current session. It deliberately stays on after an apply so the DOR can
 * apply the second discipline too.
 *
 * Apply writes a form field, not an API call: the value lands in the existing
 * dropdown, byte-identical to a manual pick, and still goes through the normal
 * Save. That is why there is no confirmation dialog.
 */
function ApplyModeFooter({
  estimate,
  ptOtOptions,
  slpOptions,
  onApplyPtOt,
  onApplySlp,
}: {
  estimate: PdpmEstimate;
  ptOtOptions: string[];
  slpOptions: string[];
  onApplyPtOt?: (optionLabel: string) => void;
  onApplySlp?: (optionLabel: string) => void;
}) {
  const [isApplyEnabled, setApplyEnabled] = useState(false);

  return (
    <Stack spacing={1}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={isApplyEnabled}
            onChange={(event) => setApplyEnabled(event.target.checked)}
            inputProps={{ 'aria-label': 'Enable apply' }}
          />
        }
        label={<Typography variant="body2">Enable apply</Typography>}
      />
      {isApplyEnabled && (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={1}
        >
          {onApplyPtOt && (
            <ApplyButton
              discipline="PT & OT"
              group={estimate.ptOtCmg?.group}
              options={ptOtOptions}
              onApply={onApplyPtOt}
            />
          )}
          {onApplySlp && (
            <ApplyButton
              discipline="SLP"
              group={estimate.slpCmg?.group}
              options={slpOptions}
              onApply={onApplySlp}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
}

function ApplyButton({
  discipline,
  group,
  options,
  onApply,
}: {
  discipline: string;
  group: string | null | undefined;
  options: string[];
  onApply: (optionLabel: string) => void;
}) {
  // the resolved facility label is what gets written, so show it on the button —
  // the DOR sees exactly what will land in the dropdown before clicking
  const resolved = resolveCmgOption(group, options);
  const label = `Apply ${discipline} CMG Estimate${resolved ? ` (${resolved})` : ''}`;

  // an off-catalog group or a failed case-mix fetch leaves nothing safe to write.
  // The estimate itself stays on screen so the DOR can still pick by hand.
  if (!resolved) {
    return (
      <Tooltip title={APPLY_UNRESOLVABLE_TOOLTIP}>
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button
      size="small"
      variant="outlined"
      onClick={() => onApply(resolved)}
    >
      {label}
    </Button>
  );
}

// completeness is not repeated here — the header carries it in every state, and
// showing the same "N of M" twice in one card reads as two different numbers
function SuppressedState() {
  return <Typography variant="body2">Not enough documented data to estimate</Typography>;
}

function CmgTile({
  title,
  group,
  subline,
  target,
}: {
  title: string;
  group: string | null;
  subline: string | null;
  target: string | null;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        height: '100%',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
      >
        {title}
      </Typography>
      <Typography variant="h6">{group || '—'}</Typography>
      {subline && (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          {subline}
        </Typography>
      )}
      {target && (
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {target}
        </Typography>
      )}
    </Box>
  );
}

function PtOtTile({ estimate, ptOtOptions }: { estimate: PdpmEstimate; ptOtOptions: string[] }) {
  const ptOt = estimate.ptOtCmg;
  const subline = [
    ptOt?.clinicalCategory,
    ptOt?.functionScore !== null && ptOt?.functionScore !== undefined
      ? `Function score ${ptOt.functionScore}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <CmgTile
      title="PT & OT CMG"
      group={ptOt?.group || null}
      subline={subline || null}
      target={formatTargetMinutes(resolveCmgOption(ptOt?.group, ptOtOptions))}
    />
  );
}

function SlpTile({ estimate, slpOptions }: { estimate: PdpmEstimate; slpOptions: string[] }) {
  const slp = estimate.slpCmg;
  const subline = [
    slp?.criterionACount !== null && slp?.criterionACount !== undefined
      ? `Criterion A ${slp.criterionACount} of 3`
      : null,
    slp?.criterionBLevel ? `Criterion B ${slp.criterionBLevel}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <CmgTile
      title="SLP CMG"
      group={slp?.group || null}
      subline={subline || null}
      target={formatTargetMinutes(resolveCmgOption(slp?.group, slpOptions))}
    />
  );
}

function WarningList({
  estimate,
  onViewInputs,
  overrideCount: pendingOverrides,
}: {
  estimate: PdpmEstimate;
  onViewInputs: () => void;
  overrideCount: number;
}) {
  const sorted = sortWarnings(estimate.warnings);
  const visible = sorted.slice(0, MAX_VISIBLE_WARNINGS);
  const hiddenCount = sorted.length - visible.length;

  return (
    <Stack spacing={1}>
      {visible.map((warning, index) => (
        <Alert
          key={`${warning.code}-${warning.itemId || index}`}
          severity={warningSeverity(warning.severity)}
        >
          {warning.message}
        </Alert>
      ))}
      {/* the drawer is always reachable: it is the derivation view for a good
          estimate and the remediation path for a suppressed one (BRK-1968) */}
      <Badge
        badgeContent={pendingOverrides}
        color="info"
        sx={{ alignSelf: 'flex-start' }}
      >
        <Button
          size="small"
          onClick={onViewInputs}
        >
          {hiddenCount > 0 ? `+${hiddenCount} more — view inputs` : 'View inputs'}
        </Button>
      </Badge>
    </Stack>
  );
}
